// Yearly items in plain words, shared by the item editor (live summary) and the screens: derived
// renewal dates, "Counts through Feb 2027" / "Stops counting Mar 2027", and the summary line.
// Pure; the engine (src/engine/spread.ts) holds the counting rules these words describe.

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-03" + 11 -> "2027-02". */
export function addMonthsTo(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const i = y * 12 + (m - 1) + n;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
}

/** Whole months from a to b. */
export const monthsFrom = (a: string, b: string) => (Number(b.slice(0, 4)) - Number(a.slice(0, 4))) * 12 + Number(b.slice(5, 7)) - Number(a.slice(5, 7));

const daysIn = (month: string) => new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
const dayIn = (month: string, day: number) => `${month}-${String(Math.min(day, daysIn(month))).padStart(2, '0')}`;

/** "Feb 2027". */
export const monthText = (month: string) => `${SHORT[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
/** "Mar 27 2027". */
export const dateText = (day: string) => `${SHORT[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))} ${day.slice(0, 4)}`;

/** Last charged Mar 27 2026: renews Mar 27 2027 (Feb 29 renews on Feb 28). */
export const renewalFromLastPaid = (lastPaidOn: string) => dayIn(addMonthsTo(lastPaidOn.slice(0, 7), 12), Number(lastPaidOn.slice(8, 10)));

/** The next date on or after `today` for a month (1 to 12) and day (the 1st when none). */
export function nextOccurrence(month: number, day: number | null, today: string): string {
  const year = Number(today.slice(0, 4));
  const at = (y: number) => dayIn(`${y}-${String(month).padStart(2, '0')}`, day ?? 1);
  return at(year) >= today ? at(year) : at(year + 1);
}

/** "Counts through Feb 2027" (or "Counted through", once it has stopped). */
export const countsThroughText = (through: string, currentMonth: string) => `${currentMonth > through ? 'Counted' : 'Counts'} through ${monthText(through)}`;
/** "Stops counting Mar 2027". */
export const stopsCountingText = (through: string) => `Stops counting ${monthText(addMonthsTo(through, 1))}`;

const cents = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface YearlyDraft {
  amountCents: number;
  spread: boolean;
  renews: boolean;
  /** "When was it last charged?" */
  lastPaidOn: string | null;
  /** "When does it renew?" (asked when there is no last charge). */
  renewsOn: string | null;
  /** A typed end month ("Change the end month"). */
  endsMonth: string | null;
  today: string;
}

/**
 * The editor's one summary line. Spread: "Counts $15.00 a month from Mar 2026 through Feb 2027.
 * 5 months left. Stops counting Mar 2027." or "Counts $15.00 a month. Renews Mar 27 2027." Whole
 * amount: "Counts $180.00 in March each year."
 */
export function yearlySummary(d: YearlyDraft): string {
  const month = d.today.slice(0, 7);
  const share = cents(Math.floor(d.amountCents / 12) + (d.amountCents % 12 ? 1 : 0));
  const renewal = d.lastPaidOn ? renewalFromLastPaid(d.lastPaidOn) : d.renewsOn;
  const start = d.lastPaidOn ? d.lastPaidOn.slice(0, 7) : null;
  // The last month it counts: a typed end month, else (will not renew) the end of the paid year.
  const through = d.endsMonth ?? (!d.renews && start ? addMonthsTo(start, 11) : null);
  const stops = through ? ` ${stopsCountingText(through)}.` : '';
  if (!d.spread) {
    const m = renewal ? MONTH_NAMES[Number(renewal.slice(5, 7)) - 1] : null;
    if (!m) return `Counts ${cents(d.amountCents)} once a year.`;
    if (!through) return `Counts ${cents(d.amountCents)} in ${m} each year.`;
    return `Counts ${cents(d.amountCents)} in ${m}, ${countsThroughText(through, month).toLowerCase()}.${stops}`;
  }
  if (!d.renews && !start && !d.endsMonth) return 'Counts nothing yet: add when it was last charged.';
  if (!through) return `Counts ${share} a month.${renewal ? ` Renews ${dateText(renewal)}.` : ''}`;
  const from = start ?? month;
  const left = monthsFrom(month, through) + 1;
  if (left <= 0) return `Counted ${share} a month from ${monthText(from)} through ${monthText(through)}. Stopped counting ${monthText(addMonthsTo(through, 1))}.`;
  return `Counts ${share} a month from ${monthText(from)} through ${monthText(through)}. ${left} ${left === 1 ? 'month' : 'months'} left.${stops}`;
}

/**
 * What the editor saves for a yearly item's schedule: the renewal month and day, from the last
 * charge (12 months on) or "When does it renew?". An item with no day of its own (any day) keeps
 * it while its renewal date is left as shown. null: neither date was given.
 */
export function yearlySchedule(a: { lastPaidOn: string | null; renewsOn: string | null; shownRenewsOn?: string | null; anyDay?: boolean }): { months: number[]; dueDay: number | null } | null {
  const renewal = a.lastPaidOn ? renewalFromLastPaid(a.lastPaidOn) : a.renewsOn;
  if (!renewal) return null;
  const keepAnyDay = !a.lastPaidOn && !!a.anyDay && a.renewsOn === a.shownRenewsOn;
  return { months: [Number(renewal.slice(5, 7))], dueDay: keepAnyDay ? null : Number(renewal.slice(8, 10)) };
}
